import { ref } from 'vue';

import useMenuCategories from '@/composables/useMenuCategories';
import useMenuItems from '@/composables/useMenuItems';

import xor from 'lodash/xor';
import isEmpty from 'lodash/isEmpty';

export default function useContent(menuId) {
  // Categories
  const {
    articles,
    category,
    categories,
    categoryMeta,
    fetchMenuCategory,
    fetchMenuCategories,
    saveMenuCategory,
    removeMenuCategory,
  } = useMenuCategories(menuId);

  const categoryById = (id) => categories.value.find((category) => category.id == id);

  const addCategory = async (type, writeMenuId) => {
    await fetchMenuCategory(type, writeMenuId);
    await fetchMenuCategories();
    const newestCatId = categories.value[categories.value.length - 1].id;
    changeEditor('cat-' + newestCatId);
  };

  const deleteCategory = async (index, moreIds) => {
    if (articlesByCategory(index).length > 0) {
      alert('Es können nur leere Kategorien gelöscht werden.');
    } else {
      await removeMenuCategory(index, moreIds);
      await fetchMenuCategories();
    }
  };

  // Articles
  const { item, fetchMenuItem, saveMenuItem, removeMenuItem } = useMenuItems(menuId);

  const articlesByCategory = (catId) => {
    return articles.value.filter((item) => item.attributes.menu_category_id == catId);
  };

  const articleById = (id) => articles.value.find((item) => item.id == id);

  const addArticle = async (type, catId) => {
    await fetchMenuItem(type, catId);
    await fetchMenuCategories();
    const articleAndSiblings = articlesByCategory(catId);
    const newestArtId = articleAndSiblings[articleAndSiblings.length - 1].id;
    changeEditor('art-' + newestArtId);
  };

  const deleteArticle = async (artId, moreIds) => {
    await removeMenuItem(artId, moreIds);
    await fetchMenuCategories();
    await checkForMissingTitles();
  };

  const checkForMissingTitles = async () => {
    let replacements = [];
    articles.value.forEach((article) => {
      if (article.attributes.data?.hiddenTitle && isEmpty(article.attributes.title)) {
        let titleFound = false;
        articlesByCategory(article.attributes.menu_category_id).some((artInCat) => {
          if (artInCat.id == article.id) return;
          if (artInCat.attributes.title == article.attributes.data.hiddenTitle) {
            return (titleFound = true);
          }
        });
        if (!titleFound) {
          article.attributes.title = article.attributes.data.hiddenTitle;
          replacements.push(article);
        }
      }
    });
    if (replacements.length > 0) {
      replacements.forEach(async (item) => await saveMenuItem(item));
    }
  };

  // Tags
  const tags = [
    { key: 'HALAL', value: 'halal' },
    // { key: 'HOT', value: 'scharf' },
    // { key: 'KOSHER', value: 'koscher' },
    { key: 'VEGAN', value: 'vegan' },
    { key: 'VEGETARIAN', value: 'vegetarisch' },
  ];

  const getSelectedTagOptions = (value) => {
    if (!value) value = '';
    const selectedTags = value.split(',');
    let selectedOptions = [];
    selectedTags.forEach((tag) => {
      tags.forEach((option) => {
        if (option.key === tag) selectedOptions.push(option);
      });
    });
    return selectedOptions;
  };

  const updateItemTags = (action, option) => {
    const id = editor.value.split('-')[1];
    articles.value.forEach(async (article, index) => {
      if (article.id == id) {
        let tags = article.attributes.tags ? article.attributes.tags.split(',') : [];
        if (action == 'select') {
          tags.push(option.key);
        } else if (action == 'remove') {
          tags = tags.filter((tag) => tag != option.key);
        }
        article.attributes.tags = tags.join(',');
        articles.value[index] = await saveMenuItem(article);
        return false;
      }
    });
  };

  // Ingredients
  const updateItemIngredients = (action, option) => {
    const id = editor.value.split('-')[1];
    articles.value.forEach(async (article, index) => {
      if (article.id == id) {
        if (action == 'select') {
          article.relationships.ingredients.data.push({
            id: option.key,
            type: 'ingredients',
          });
        } else if (action == 'remove') {
          article.relationships.ingredients.data = article.relationships.ingredients.data.filter(
            (ingredient) => ingredient.id != option.key
          );
        }
        articles.value[index] = await saveMenuItem(article);
        return false;
      }
    });
  };

  // Editor
  const editor = ref('');

  const changeEditor = (target) => {
    if (editor.value == target) {
      editor.value = '';
    } else {
      editor.value = target;
      setTimeout(() => {
        const [type, id] = target.split('-');
        if (type == 'cat' && !categoryIsOpen(id)) {
          toggleCategory(id);
        }
        const selector = `[${type == 'cat' ? 'data-category' : 'data-article'}="${id}"]`;
        document.querySelector(selector + ' input')?.focus();
      }, 100);
    }
  };

  // Collapsable Categories
  const openCategories = ref([]);

  const toggleCategory = (catId) => {
    openCategories.value = xor(openCategories.value, [].concat(catId));
  };

  const closeAllCategories = () => {
    toggleCategory(openCategories.value);
  };

  const openAllCategories = () => {
    closeAllCategories();
    toggleCategory(categories.value.map((category) => category.id));
  };

  const categoryIsOpen = (catId) => {
    return openCategories.value.indexOf(catId) >= 0;
  };

  //DataChange Handler
  const updateData = (event) => {
    const [type, id, field] = event.target.id.split('-');
    const obj = { cat: categories, art: articles };
    const func = { cat: saveMenuCategory, art: saveMenuItem };
    obj[type].value.forEach(async (element, index) => {
      if (element.id == id) {
        element.attributes[field] =
          event.target.value === null || event.target.value === '' || isNaN(event.target.value)
            ? event.target.value
            : Number(event.target.value);
        obj[type].value[index] = await func[type](element);
        return false;
      }
    });
  };

  return {
    addArticle,
    addCategory,
    articleById,
    articles,
    articlesByCategory,
    categories,
    categoryMeta,
    category,
    categoryById,
    categoryIsOpen,
    changeEditor,
    closeAllCategories,
    deleteArticle,
    deleteCategory,
    editor,
    fetchMenuCategories,
    fetchMenuCategory,
    fetchMenuItem,
    getSelectedTagOptions,
    item,
    openAllCategories,
    openCategories,
    removeMenuCategory,
    removeMenuItem,
    saveMenuCategory,
    saveMenuItem,
    tags,
    toggleCategory,
    updateData,
    updateItemIngredients,
    updateItemTags,
  };
}
